import { useId, useMemo, useState, type FC } from 'react';
import { ArrowLeft, X } from 'lucide-react';
import {
  Alert,
  Button,
  Checkbox,
  Combobox,
  Dialog,
  Skeleton,
  useDebouncedValue,
} from '@/ui';

import {
  resolveApiErrorMessage,
  resolveApiValidationMessages,
} from '../../lib/apiError';
import { useSetPartnerPlanning } from '../customers';
import {
  useCreateShipmentsFromForecast,
  usePartnerOptions,
  useWarehouses,
  type PartnerOption,
} from '../shipment-actions';
import { useShipmentForecast } from './api/queries';
import { ForecastProposals } from './ForecastProposals';
import {
  applyOverride,
  buildForecastQuery,
  buildShipmentsToCreate,
  type ForecastOverrides,
  type ForecastSelection,
} from './lib/forecast';

/*
 * «Сформировать из истории» - мастер в два шага (plan/shipment-plan-from-history):
 * клиенты, затем предложенные отгрузки. План считает сервер по ритму поставок
 * каждого товара; экран хранит только правки и галочки, а выбранное создаёт
 * одним запросом - все отгрузки или ни одной.
 *
 * Окно монтируется только открытым (родитель рендерит его по условию), поэтому
 * клиенты, правки и галочки сбрасываются сами при закрытии.
 */

export interface HistoryPlanDialogProps {
  onClose: () => void;
}

type Step = 'clients' | 'proposals';

const SEARCH_DEBOUNCE_MS = 250;

const formatCreateLabel = (count: number): string => {
  if (count % 10 === 1 && count % 100 !== 11)
    return `Создать ${count} отгрузку`;
  if ([2, 3, 4].includes(count % 10) && ![12, 13, 14].includes(count % 100))
    return `Создать ${count} отгрузки`;
  return `Создать ${count} отгрузок`;
};

export const HistoryPlanDialog: FC<HistoryPlanDialogProps> = ({ onClose }) => {
  const clientsFieldId = useId();
  const allClientsFieldId = useId();

  const [step, setStep] = useState<Step>('clients');
  const [clients, setClients] = useState<ReadonlyArray<PartnerOption>>([]);
  const [isAllClients, setIsAllClients] = useState(false);
  const [overrides, setOverrides] = useState<ForecastOverrides>(new Map());
  const [selection, setSelection] = useState<ForecastSelection>(new Map());
  const [partnerQuery, setPartnerQuery] = useState('');

  const partners = usePartnerOptions(
    useDebouncedValue(partnerQuery, SEARCH_DEBOUNCE_MS),
  );
  const warehouses = useWarehouses();

  const query = useMemo(
    () =>
      step === 'proposals'
        ? buildForecastQuery(
            clients.map((client) => client.id),
            isAllClients,
            overrides,
          )
        : null,
    [step, clients, isAllClients, overrides],
  );

  const forecast = useShipmentForecast(query);
  const create = useCreateShipmentsFromForecast();
  const exclude = useSetPartnerPlanning();

  const shipmentsToCreate =
    forecast.data === undefined
      ? []
      : buildShipmentsToCreate(forecast.data, selection);
  const canCompute = isAllClients || clients.length > 0;

  const partnerOptions: ReadonlyArray<PartnerOption> = (
    partners.data?.items ?? []
  )
    .filter((partner) => !partner.isExcludedFromPlanning)
    .map((partner) => ({
      id: partner.id,
      name: partner.name,
      code: partner.code,
    }));

  const createMessages = create.isError
    ? resolveApiValidationMessages(create.error)
    : [];
  const createError =
    create.isError && createMessages.length === 0
      ? resolveApiErrorMessage(create.error)
      : undefined;

  const handleCreate = () =>
    create.mutate(
      { body: { shipments: shipmentsToCreate } },
      { onSuccess: onClose },
    );

  const handleExclude = (partnerId: string) =>
    exclude.mutate(
      { path: { id: partnerId }, body: { isExcludedFromPlanning: true } },
      {
        onSuccess: () =>
          setClients(clients.filter((client) => client.id !== partnerId)),
      },
    );

  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
      widthClassName="w-[min(1040px,96vw)]"
      title="Сформировать план из истории"
      description={
        step === 'clients'
          ? 'Шаг 1 из 2: клиенты. По каждому товару план продолжит ритм прошлых поставок.'
          : 'Шаг 2 из 2: предложенные отгрузки. Отметьте нужные и поправьте дату, склад, количество или интервал.'
      }
      footer={
        step === 'clients' ? (
          <>
            <Button onClick={onClose}>Отмена</Button>
            <Button
              variant="primary"
              disabled={!canCompute}
              onClick={() => setStep('proposals')}
            >
              Рассчитать план
            </Button>
          </>
        ) : (
          <>
            <Button className="mr-auto" onClick={() => setStep('clients')}>
              <ArrowLeft aria-hidden /> Клиенты
            </Button>
            <Button onClick={onClose}>Отмена</Button>
            <Button
              variant="primary"
              disabled={
                shipmentsToCreate.length === 0 ||
                create.isPending ||
                forecast.isFetching
              }
              onClick={handleCreate}
            >
              {create.isPending
                ? 'Создаём...'
                : formatCreateLabel(shipmentsToCreate.length)}
            </Button>
          </>
        )
      }
    >
      {step === 'clients' ? (
        // Тело окна прокручивается и обрезает выпадающий список поля выбора (до 256px),
        // а на этом шаге оно низкое: высота с запасом под раскрытый список.
        <div className="flex min-h-[340px] flex-col gap-3">
          <div className="flex flex-col gap-1.5">
            <label
              htmlFor={clientsFieldId}
              className="text-xs font-medium text-ink-700"
            >
              Клиенты
            </label>
            <Combobox
              id={clientsFieldId}
              value={null}
              onChange={(partner) => {
                const isNew =
                  partner !== null &&
                  !clients.some((client) => client.id === partner.id);
                if (isNew) setClients([...clients, partner]);
              }}
              onQueryChange={setPartnerQuery}
              options={partnerOptions}
              isLoading={partners.isFetching}
              getKey={(partner) => partner.id}
              getLabel={(partner) => partner.name}
              placeholder="Найти клиента по названию или коду"
              emptyLabel="Клиент не найден"
              disabled={isAllClients}
            />
          </div>

          {clients.length > 0 && !isAllClients && (
            <ul className="m-0 flex list-none flex-wrap gap-1.5 p-0">
              {clients.map((client) => (
                <li
                  key={client.id}
                  className="inline-flex items-center gap-1 rounded-full bg-ink-100 py-1 pr-1 pl-2.5 text-[12.5px] text-ink-800"
                >
                  {client.name}
                  <button
                    type="button"
                    aria-label={`Убрать клиента ${client.name}`}
                    onClick={() =>
                      setClients(
                        clients.filter((item) => item.id !== client.id),
                      )
                    }
                    className="inline-flex size-5 cursor-pointer items-center justify-center rounded-full text-ink-500 hover:bg-ink-200 hover:text-ink-900"
                  >
                    <X aria-hidden className="size-3" />
                  </button>
                </li>
              ))}
            </ul>
          )}

          <span className="inline-flex items-center gap-2">
            <Checkbox
              id={allClientsFieldId}
              checked={isAllClients}
              onChange={(event) => setIsAllClients(event.target.checked)}
            />
            <label
              htmlFor={allClientsFieldId}
              className="text-[13px] text-ink-800"
            >
              Все клиенты, кроме исключённых из планирования
            </label>
          </span>
        </div>
      ) : (
        <div className="flex flex-col gap-3">
          {forecast.isPending && (
            <div className="flex flex-col gap-2">
              <Skeleton className="h-6 w-72" />
              <Skeleton className="h-32" />
              <Skeleton className="h-32" />
            </div>
          )}
          {forecast.isError && (
            <Alert tone="error">
              Не удалось рассчитать план:{' '}
              {resolveApiValidationMessages(forecast.error).join(' ') ||
                resolveApiErrorMessage(forecast.error)}
            </Alert>
          )}
          {exclude.isError && (
            <Alert tone="error">
              Не удалось исключить клиента:{' '}
              {resolveApiErrorMessage(exclude.error)}
            </Alert>
          )}
          {forecast.data !== undefined && (
            <ForecastProposals
              forecast={forecast.data}
              selection={selection}
              onSelectionChange={setSelection}
              onEdit={(entries, patch) =>
                setOverrides(applyOverride(overrides, entries, patch))
              }
              warehouses={warehouses.data ?? []}
              onExclude={handleExclude}
              isExcluding={exclude.isPending}
              isRecomputing={forecast.isFetching}
            />
          )}
          {(createMessages.length > 0 || createError !== undefined) && (
            <Alert tone="error">
              {createError ?? (
                <ul className="m-0 list-none p-0">
                  {createMessages.map((message) => (
                    <li key={message}>{message}</li>
                  ))}
                </ul>
              )}
            </Alert>
          )}
        </div>
      )}
    </Dialog>
  );
};
